/**
 * Una consulta del fan-out. **Componente propio**, composición de piezas del DS.
 *
 * Es la unidad visual del motor: un requerimiento abre N consultas —una por
 * canal— y la primera respuesta concluyente cierra el requerimiento y abandona
 * a sus hermanas (`NEG-8`). Esta tarjeta tiene que dejar ver, de un vistazo,
 * cuál resolvió y cuáles quedaron abandonadas.
 *
 * `UI-8`: la abandonada se atenúa pero **se sigue leyendo**. No se oculta: el
 * trabajo que costó abrirla y los pasos que llegó a recorrer son parte de la
 * traza, y ante una auditoría hay que poder explicar por qué se dejó de
 * esperar por ese canal.
 */
import type { ReactNode } from 'react'
import { Card, CardContent, CardHeader, StatusBadge } from '@mpfn/ui'
import { ResponsibilityBadge } from '@mpfn/patterns/experimental'
import { Paperclip } from 'lucide-react'
import { useCatalogos, etiquetaDe, pasoDe } from '../api/catalogos.js'
import type { Consulta } from '../api/tipos.js'
import { urlDeArchivo } from '../api/cliente.js'
import { diasHabiles, fecha } from '../lib/formato.js'
import { BadgePaso } from './BadgePaso.js'

interface PropsTarjeta {
  consulta: Consulta
  /** `NEG-8`: la que cerró el requerimiento. Se destaca con borde de acento. */
  esResolutoria?: boolean
  /** Controles de escritura. Ausentes en modo solo lectura (`UI-9`). */
  acciones?: ReactNode
}

export function TarjetaConsulta({ consulta, esResolutoria = false, acciones }: PropsTarjeta) {
  const catalogos = useCatalogos()
  const abandonada = consulta.desenlace === 'abandonada'
  const pasoActual = pasoDe(catalogos, consulta.canal, consulta.paso)
  // A quién le toca mover. Lo decide el backend con `siguientePaso` (`ARQ-4`);
  // aquí solo se busca su descripción y su actor en el catálogo (`ARQ-8`).
  const siguiente =
    consulta.siguientePaso !== undefined
      ? pasoDe(catalogos, consulta.canal, consulta.siguientePaso)
      : undefined

  const evidencias = [
    ...consulta.pasos
      .filter((paso) => paso.evidenciaId)
      .map((paso) => ({ id: paso.evidenciaId ?? '', titulo: `Paso ${String(paso.paso)}` })),
    ...(consulta.respuesta
      ? [{ id: consulta.respuesta.evidenciaId, titulo: 'Respuesta de la entidad' }]
      : []),
  ]

  return (
    <Card
      className="sigfisc-consulta"
      data-resolutoria={esResolutoria ? 'si' : 'no'}
      data-abandonada={abandonada ? 'si' : 'no'}
      data-anulada={consulta.anulada ? 'si' : 'no'}
    >
      <CardHeader className="sigfisc-consulta__cabecera">
        <div className="sigfisc-consulta__canal">
          <span className="sigfisc-consulta__etiqueta">{consulta.canalEtiqueta}</span>
          <span className="sigfisc-consulta__destino" title={consulta.destinoTexto}>
            {consulta.destinoTexto}
          </span>
        </div>
        <StatusBadge
          tone={consulta.anulada ? 'neutral' : consulta.tono}
          label={
            consulta.anulada
              ? 'Anulada'
              : consulta.desenlace
                ? etiquetaDe(catalogos.desenlacesConsulta, consulta.desenlace)
                : etiquetaDe(catalogos.estadosConsulta, consulta.estado)
          }
        />
      </CardHeader>

      <CardContent className="sigfisc-consulta__cuerpo">
        {/**
         * El progreso se oculta en las ramas terminales NEGATIVAS.
         *
         * `badgeDePaso` del servidor devuelve `0` a propósito cuando el
         * desenlace es `sin_respuesta` o `no_autentico`: son ramas terminales
         * alternativas, no una continuación del circuito, y es la convención de
         * Recuperos y del prototipo (`A10`). El número es correcto.
         *
         * Lo que no funciona es enseñarlo: un «0/9» con los nueve puntos en
         * gris, justo encima de la lista de los nueve pasos con su fecha, se
         * lee como que la pantalla se equivocó. La insignia de estado que está
         * al lado ya dice el desenlace, y el recorrido está debajo; el contador
         * solo añade contradicción.
         */}
        {consulta.desenlace !== 'sin_respuesta' && consulta.desenlace !== 'no_autentico' && (
          <BadgePaso
            paso={consulta.paso}
            totalPasos={consulta.totalPasos}
            tono={consulta.tono}
            {...(pasoActual ? { descripcion: pasoActual.descripcion } : {})}
          />
        )}

        {/**
         * De quién es el turno. `ResponsibilityBadge` del design system.
         *
         * Es la pregunta que el circuito plantea en cada consulta abierta
         * —¿está parado en Mesa de Partes o en el notificador?— y hasta ahora
         * no estaba escrita en ninguna parte: había que contar los puntos del
         * `BadgePaso` y saberse de memoria a quién le toca el paso siguiente
         * de ese canal. El dato ya venía en la proyección (`siguientePaso`).
         *
         * Solo mientras haya turno: una consulta con desenlace o anulada ya no
         * espera a nadie, y decir que «el responsable actual es el notificador»
         * de algo que se cerró sería falso.
         */}
        {siguiente && !consulta.desenlace && !consulta.anulada && (
          <ResponsibilityBadge
            responsible={siguiente.actorEtiqueta}
            description={siguiente.descripcion}
          />
        )}

        <dl className="sigfisc-consulta__datos">
          <div>
            <dt>Apertura</dt>
            <dd>{fecha(consulta.fechaApertura)}</dd>
          </div>
          {consulta.numeroCargo !== undefined && (
            <div>
              <dt>N.º de cargo</dt>
              <dd>{consulta.numeroCargo}</dd>
            </div>
          )}
          {consulta.fechaVencimiento !== undefined && (
            <div>
              <dt>Vence</dt>
              <dd>
                {fecha(consulta.fechaVencimiento)}
                {consulta.diasHabilesRestantes !== undefined && (
                  <span className="sigfisc-consulta__plazo" data-vencida={consulta.vencida ? 'si' : 'no'}>
                    {' · '}
                    {diasHabiles(consulta.diasHabilesRestantes)}
                  </span>
                )}
              </dd>
            </div>
          )}
        </dl>

        {consulta.pasos.length > 0 && (
          <ol className="sigfisc-consulta__pasos">
            {consulta.pasos.map((paso) => (
              <li key={paso.paso}>
                <span className="sigfisc-consulta__paso-numero">{paso.paso}</span>
                <span className="sigfisc-consulta__paso-texto">
                  {paso.descripcion}
                  <span className="sigfisc-consulta__paso-fecha">{fecha(paso.fecha)}</span>
                </span>
              </li>
            ))}
          </ol>
        )}

        {consulta.respuesta && (
          <p className="sigfisc-consulta__respuesta">
            <strong>{etiquetaDe(catalogos.pronunciamientos, consulta.respuesta.pronunciamiento)}</strong>
            {' · '}
            {fecha(consulta.respuesta.fechaRecepcion)}
            {consulta.respuesta.referencia !== undefined && ` · ${consulta.respuesta.referencia}`}
            <span className="sigfisc-consulta__observacion">{consulta.respuesta.observacion}</span>
          </p>
        )}

        {abandonada && (
          <p className="sigfisc-consulta__nota">
            Se dejó de esperar por este canal: otra consulta del mismo requerimiento resolvió antes.
          </p>
        )}

        {/**
         * Cerrada sin respuesta: se dice QUÉ pasó y QUÉ se puede hacer.
         *
         * Antes solo quedaba la insignia «Sin respuesta» y la tarjeta sin un
         * solo botón —porque ya no admite ninguno—, con lo que la pantalla no
         * ofrecía salida ni explicaba que la hubiera. La salida existe y es
         * reiterar el requerimiento.
         */}
        {consulta.desenlace === 'sin_respuesta' && (
          <p className="sigfisc-consulta__nota">
            Se cerró sin que la entidad respondiera, así que este canal ya no admite respuesta.
            Para volver a preguntar, reitere el requerimiento desde el documento.
          </p>
        )}

        {evidencias.length > 0 && (
          <ul className="sigfisc-consulta__evidencias">
            {evidencias.map((evidencia) => (
              <li key={evidencia.id}>
                <a
                  className="mpfn-control sigfisc-consulta__evidencia"
                  href={urlDeArchivo(evidencia.id)}
                  download
                >
                  <Paperclip size={14} aria-hidden="true" />
                  {evidencia.titulo}
                </a>
              </li>
            ))}
          </ul>
        )}

        {acciones && <div className="sigfisc-consulta__acciones">{acciones}</div>}
      </CardContent>
    </Card>
  )
}
